<template>
  <div class="doc-content-viewer">
    <div v-if="loading" class="doc-content-state">
      <Spin size="large">加载文档...</Spin>
    </div>

    <div v-else-if="!doc" class="doc-content-state doc-content-state--empty">
      <BaseEmpty type="empty" msg="请从左侧选择文档" />
    </div>

    <template v-else>
      <div class="doc-content-header">
        <div class="doc-content-title-wrap">
          <BaseIcon :icon="icon" :size="16" class="doc-content-title-icon" />
          <h1 class="doc-content-title" :title="updatedText ? `${title} · ${updatedText}` : title">
            {{ title }}
          </h1>
        </div>
      </div>

      <div
        class="doc-content-main"
        :class="{
          'has-toc': showToc,
          'doc-content-main--fill': isFillPreview,
        }"
      >
        <div class="doc-content-body" :class="{ 'doc-content-body--fill': isFillPreview }">
          <div v-if="viewType === 'markdown'" class="doc-markdown-wrap">
            <BaseMarkdownView ref="markdownBody" :value="markdownSource" />
          </div>

          <div v-else-if="viewType === 'file'" class="doc-file-wrap">
            <BasePreview :url="fileUrl" :name="title" />
          </div>

          <div v-else-if="viewType === 'text'" class="doc-text-wrap">
            <pre class="doc-text-content">{{ textContent }}</pre>
          </div>

          <div v-else-if="viewType === 'html'" class="doc-html-wrap">
            <div class="doc-html-content" v-html="textContent"></div>
          </div>

          <div v-else class="doc-content-state">
            <BaseEmpty type="content" msg="该文件格式暂不支持在线预览，请下载后查看">
              <Button v-if="fileUrl" type="primary" ghost icon="md-download" @click="downloadFile">
                下载文件
              </Button>
            </BaseEmpty>
          </div>
        </div>

        <aside v-if="showToc" class="doc-content-toc">
          <div class="doc-toc-title">目录</div>
          <nav class="doc-toc-nav">
            <a
              v-for="item in headings"
              :key="item.id"
              class="doc-toc-link"
              :class="{ 'is-active': activeHeadingId === item.id }"
              :style="{ paddingLeft: `${(item.level - 1) * 12 + 8}px` }"
              @click.prevent="scrollToHeading(item.id)"
            >
              {{ item.text }}
            </a>
          </nav>
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
import BaseEmpty from '@/components/base/Empty';
import BaseIcon from '@/components/base/Icon';
import BaseMarkdownView from '@/components/base/MarkdownView';
import BasePreview from '@/components/base/Preview';
import {
  extractHeadings,
  getDocumentIcon,
  getDocumentTitle,
  getDocumentUrl,
  getMarkdownContent,
  isMdDocument,
  looksLikeMarkdownContent,
} from './document-helpers';

export default {
  name: 'DocumentContentViewer',
  components: {BaseEmpty, BaseIcon, BaseMarkdownView, BasePreview},
  props: {
    doc: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeHeadingId: '',
      scrollHandler: null,
    };
  },
  computed: {
    title() {
      return getDocumentTitle(this.doc);
    },
    icon() {
      return getDocumentIcon(this.doc);
    },
    fileUrl() {
      return getDocumentUrl(this.doc);
    },
    textContent() {
      if (!this.doc) return '';
      const content = this.doc.content || '';
      if (/^https?:\/\//i.test(content)) return this.doc.markdown_content || '';
      return content;
    },
    viewType() {
      if (!this.doc) return '';
      const type = String(this.doc.type || '').toLowerCase();
      const mdContent = getMarkdownContent(this.doc);
      if (type === 'md' || type === 'markdown') return 'markdown';
      if (type === 'file') return 'file';
      if (type === 'html') return 'html';
      if (type === 'text') {
        if (isMdDocument(this.doc) || looksLikeMarkdownContent(mdContent)) {
          return 'markdown';
        }
        return 'text';
      }
      const content = String(this.doc.content || '').trim();
      if (content && /^https?:\/\//i.test(content)) return 'file';
      if (isMdDocument(this.doc) || this.doc.has_markdown || looksLikeMarkdownContent(mdContent)) {
        return 'markdown';
      }
      if (content || this.doc.markdown_content) return 'markdown';
      return 'unknown';
    },
    markdownSource() {
      return getMarkdownContent(this.doc) || this.doc.markdown_content || '';
    },
    headings() {
      if (this.viewType !== 'markdown') return [];
      return extractHeadings(this.markdownSource);
    },
    showToc() {
      return this.viewType === 'markdown' && this.headings.length > 0;
    },
    updatedText() {
      if (!this.doc || !this.doc.updated) return '';
      return `更新于 ${this.moment(this.doc.updated).format('YYYY-MM-DD HH:mm')}`;
    },
    isFillPreview() {
      return this.viewType === 'file' || this.viewType === 'markdown';
    },
  },
  watch: {
    doc() {
      this.activeHeadingId = '';
      this.$nextTick(() => this.bindScrollSpy());
    },
    markdownSource() {
      this.$nextTick(() => this.bindScrollSpy());
    },
  },
  mounted() {
    this.bindScrollSpy();
  },
  beforeDestroy() {
    this.unbindScrollSpy();
  },
  methods: {
    getMarkdownRoot() {
      const vm = this.$refs.markdownBody;
      if (!vm) return null;
      return typeof vm.getRootEl === 'function' ? vm.getRootEl() : vm.$el;
    },
    scrollToHeading(id) {
      const root = this.getMarkdownRoot();
      if (!root) return;
      const target = root.querySelector(`#${CSS.escape(id)}`);
      if (target) {
        target.scrollIntoView({behavior: 'smooth', block: 'start'});
        this.activeHeadingId = id;
      }
    },
    bindScrollSpy() {
      this.unbindScrollSpy();
      this.$nextTick(() => {
        const root = this.getMarkdownRoot();
        if (!root || !this.headings.length) return;
        const container = root.closest('.doc-content-body');
        if (!container) return;
        this.scrollHandler = () => {
          const scrollTop = container.scrollTop;
          let current = this.headings[0] ? this.headings[0].id : '';
          this.headings.forEach((item) => {
            const el = root.querySelector(`#${CSS.escape(item.id)}`);
            if (el && el.offsetTop - 24 <= scrollTop) {
              current = item.id;
            }
          });
          this.activeHeadingId = current;
        };
        container.addEventListener('scroll', this.scrollHandler, {passive: true});
        this.scrollHandler();
      });
    },
    unbindScrollSpy() {
      const root = this.getMarkdownRoot();
      const container = root && root.closest('.doc-content-body');
      if (container && this.scrollHandler) {
        container.removeEventListener('scroll', this.scrollHandler);
      }
      this.scrollHandler = null;
    },
    downloadFile() {
      if (this.fileUrl) {
        this.Download(this.fileUrl);
      }
    },
  },
};
</script>

<style lang="less" scoped>
.doc-content-viewer {
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--bg2);
}

.doc-content-state {
  flex: 1;
  align-self: stretch;
  width: 100%;
  min-height: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.doc-content-state--empty {
  background: rgba(120, 120, 120, 0.14);

  /deep/ .l-empty-holder {
    width: 100%;
    height: 100%;
    min-height: 100%;
    opacity: 0.55;
    filter: grayscale(1);
  }

  /deep/ .l-empty-message {
    color: var(--subtext3);
  }
}

.doc-content-header {
  flex-shrink: 0;
  height: 30px;
  min-height: 30px;
  max-height: 30px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid var(--border);
  box-sizing: border-box;
}

.doc-content-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.doc-content-title-icon {
  flex-shrink: 0;
  color: var(--primary);
}

.doc-content-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 30px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doc-content-main {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
}

.doc-content-main--fill {
  min-height: 0;
}

.doc-content-main.has-toc {
  .doc-content-body {
    flex: 1;
    min-width: 0;
  }
}

.doc-content-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px 20px 24px;
}

.doc-content-body--fill {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.doc-markdown-wrap {
  width: 100%;
  max-width: none;
  margin: 0;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  padding: 12px 16px 16px;
  overflow: auto;
}

.doc-file-wrap {
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  margin: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;

  /deep/ .base-preview-root {
    flex: 1;
    min-height: 0;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    overflow: hidden;
  }

  /deep/ .base-preview {
    min-height: 0;
    min-width: 0;
    max-width: 100%;
  }

  /deep/ .base-preview--iframe,
  /deep/ .base-preview--pdf {
    max-width: 100%;
    overflow: hidden;
  }

  /deep/ .base-preview__iframe,
  /deep/ .base-preview__pdf-object {
    min-height: 100%;
    max-width: 100%;
  }

  /deep/ .base-preview__image {
    max-height: 100%;
  }
}

.doc-text-wrap,
.doc-html-wrap {
  width: 100%;
  max-width: none;
  margin: 0;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  padding: 12px 16px 16px;
  overflow: auto;
}

.doc-text-content {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 24px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: Consolas, 'Courier New', monospace;
  line-height: 1.7;
}

.doc-html-content {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 24px;
}

.doc-content-toc {
  width: 220px;
  flex-shrink: 0;
  border-left: 1px solid var(--border);
  background: #fff;
  padding: 20px 0 24px;
  overflow: auto;
}

.doc-toc-title {
  padding: 0 16px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.doc-toc-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.doc-toc-link {
  display: block;
  padding: 6px 16px 6px 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #606266;
  text-decoration: none;
  border-left: 2px solid transparent;
  transition: all 0.15s ease;

  &:hover {
    color: #3399ff;
    background: rgba(51, 153, 255, 0.06);
  }

  &.is-active {
    color: #3399ff;
    border-left-color: #3399ff;
    background: rgba(51, 153, 255, 0.08);
    font-weight: 500;
  }
}
</style>
